import { IMessage } from "atlas-shared";
import { useEffect, useRef, useCallback, useState } from "react";

export const useConversationScroll = (
  scrollRef: React.RefObject<HTMLDivElement>,
  options: {
    hasActiveChatSession: boolean;
    chatMessages: IMessage[];
    isInitialLoad: boolean;
  }
) => {
  const { hasActiveChatSession, chatMessages, isInitialLoad } = options;
  const lastChatMessageCountRef = useRef(0);
  const hasScrolledManuallyRef = useRef(false);
  const initialScrollSetRef = useRef(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [scrolled, setScrolled] = useState(false);

  const checkIfUserScrolled = useCallback(() => {
    if (!scrollRef.current) return false;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const threshold = hasActiveChatSession ? 120 : 10;
    const isAtBottomNow = scrollTop + clientHeight >= scrollHeight - threshold;
    setIsAtBottom(isAtBottomNow);
    return !isAtBottomNow;
  }, [scrollRef, hasActiveChatSession]);

  const scrollToBottom = useCallback(
    (smooth = true) => {
      if (scrollRef.current) {
        scrollRef.current.scrollTo({
          top: scrollRef.current.scrollHeight,
          behavior: smooth ? "smooth" : "auto",
        });
        setIsAtBottom(true);
        hasScrolledManuallyRef.current = false;
      }
    },
    [scrollRef]
  );

  const scrollToTop = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: 0,
        behavior: "auto",
      });
      setIsAtBottom(false);
    }
  }, [scrollRef]);

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    const handleScroll = () => {
      if (!initialScrollSetRef.current) return;
      hasScrolledManuallyRef.current = checkIfUserScrolled();
      setScrolled(true);
      setTimeout(() => setScrolled(false), 100);
    };

    scrollElement.addEventListener("scroll", handleScroll);
    return () => scrollElement.removeEventListener("scroll", handleScroll);
  }, [checkIfUserScrolled, scrollRef]);

  useEffect(() => {
    if (isInitialLoad && !initialScrollSetRef.current) {
      setTimeout(() => {
        if (hasActiveChatSession) {
          scrollToBottom(false);
        } else {
          scrollToTop();
        }
        initialScrollSetRef.current = true;
      }, 250);
    }
  }, [isInitialLoad, hasActiveChatSession, scrollToBottom, scrollToTop]);

  useEffect(() => {
    if (!hasActiveChatSession || !initialScrollSetRef.current) return;

    const currentChatMessageCount = chatMessages.length;
    const hasNewChatMessages = currentChatMessageCount > lastChatMessageCountRef.current;

    if (hasNewChatMessages) {
      if (!hasScrolledManuallyRef.current) {
        scrollToBottom(true);
      }
      lastChatMessageCountRef.current = currentChatMessageCount;
    }
  }, [hasActiveChatSession, chatMessages.length, scrollToBottom]);

  useEffect(() => {
    if (hasActiveChatSession && initialScrollSetRef.current && !hasScrolledManuallyRef.current) {
      setTimeout(() => {
        scrollToBottom(false);
      }, 100);
    }
  }, [hasActiveChatSession, scrollToBottom]);

  return {
    scrollToBottom,
    scrollToTop,
    hasScrolledManually: hasScrolledManuallyRef.current,
    isAtBottom,
    scrolled,
  };
};
